<template>
  <Loader v-if="loader" />
  <div class="page">
    <header class="page-header">
      <div>
        <p class="eyebrow">Fatturazione</p>
        <h1>Validazioni fatture</h1>
      </div>
    </header>

    <nav class="view-tabs" aria-label="Sezioni">
      <button
        type="button"
        class="view-tab"
        :class="{ active: activeView === 'attivita' }"
        @click="activeView = 'attivita'"
      >
        Attività validatori
      </button>
      <button
        type="button"
        class="view-tab"
        :class="{ active: activeView === 'periodo' }"
        @click="switchToPeriodo"
      >
        Analisi per periodo
      </button>
    </nav>

    <!-- ========== VISTA 1: ATTIVITÀ VALIDATORI ========== -->
    <section v-if="activeView === 'attivita'" class="view-panel">
      <div class="metrics">
        <div class="metric">
          <span class="metric-label">Totale fatture</span>
          <span class="metric-value">{{ formatNumber(totaleFatture) }}</span>
        </div>
        <div class="metric">
          <span class="metric-label">Validate</span>
          <span class="metric-value">{{ formatNumber(totaleValidate) }}</span>
        </div>
        <div class="metric tone-warn">
          <span class="metric-label">Non validate</span>
          <span class="metric-value">{{ formatNumber(nonAncoraValidate) }}</span>
        </div>
        <div class="metric tone-danger">
          <span class="metric-label">Respinte</span>
          <span class="metric-value">{{ formatNumber(validateEsito0) }}</span>
        </div>
        <div class="metric tone-ok">
          <span class="metric-label">Confermate</span>
          <span class="metric-value">{{ formatNumber(validateEsito1) }}</span>
        </div>
        <div class="metric">
          <span class="metric-label">Media / giorno</span>
          <span class="metric-value">{{ formatNumber(mediaValidazioniGiorno) }}</span>
        </div>
      </div>

      <div class="panel">
        <div class="panel-toolbar">
          <div>
            <h2>Dettaglio per validatore</h2>
            <p class="panel-hint">Clicca una riga per aprire le fatture di quel giorno.</p>
          </div>
          <div class="toolbar-actions">
            <Calendar
              v-model="dateRange"
              selectionMode="range"
              dateFormat="dd/mm/yy"
              placeholder="Intervallo date"
              showIcon
              class="filter-calendar"
            />
            <button
              type="button"
              class="btn btn-secondary"
              :class="{ active: mostraTuttiDettagli }"
              @click="toggleMostraTutto"
            >
              {{ mostraTuttiDettagli ? 'Chiudi esploso' : 'Mostra tutto' }}
            </button>
          </div>
        </div>

        <div class="table-wrap" v-if="!mostraTuttiDettagli">
          <table class="data-table">
            <thead>
              <tr>
                <th @click="setSort('n_fatture_validate')" class="sortable">
                  N. validate
                  <span v-if="sortKey === 'n_fatture_validate'">{{ sortAsc ? '↑' : '↓' }}</span>
                </th>
                <th>Validatore</th>
                <th @click="setSort('data')" class="sortable">
                  Giorno
                  <span v-if="sortKey === 'data'">{{ sortAsc ? '↑' : '↓' }}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in dettaglioPaginato"
                :key="row.data + row.user_id"
                @click="mostraDettaglio(row)"
                :class="{ selected: dettaglioSelezionato && dettaglioSelezionato.row === row }"
              >
                <td><span class="count-pill">{{ row.n_fatture_validate }}</span></td>
                <td>
                  <div class="user-cell">
                    <strong>{{ row.nome }}</strong>
                    <span>{{ row.email }}</span>
                  </div>
                </td>
                <td class="mono">{{ formatDate(row.data) }}</td>
              </tr>
              <tr v-if="!dettaglioPaginato.length">
                <td colspan="3" class="empty-cell">Nessun record per i filtri selezionati</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="pagination" v-if="!mostraTuttiDettagli && totalPages > 0">
          <button type="button" @click="goToPage(currentPage - 1)" :disabled="currentPage === 1">‹</button>
          <button
            v-for="page in visiblePages(currentPage, totalPages)"
            :key="'p-' + page"
            type="button"
            @click="goToPage(page)"
            :class="['page-btn', { active: page === currentPage }]"
          >
            {{ page }}
          </button>
          <button type="button" @click="goToPage(currentPage + 1)" :disabled="currentPage === totalPages">›</button>
          <span class="pagination-total">{{ dettaglioFiltrato.length }} record</span>
        </div>

        <!-- Esploso riga -->
        <div v-if="dettaglioSelezionato && !mostraTuttiDettagli" class="drilldown">
          <div class="drilldown-header">
            <div>
              <h3>{{ dettaglioSelezionato.row.nome }}</h3>
              <p>{{ formatDate(dettaglioSelezionato.row.data) }} · {{ dettaglioSelezionato.row.n_fatture_validate }} fatture</p>
            </div>
            <button type="button" class="btn btn-ghost" @click="dettaglioSelezionato = null">Chiudi</button>
          </div>
          <input
            v-model="searchEsploso"
            class="search-input"
            type="text"
            placeholder="Cerca nei dettagli..."
          />
          <div class="table-wrap">
            <table class="data-table compact">
              <thead>
                <tr>
                  <th>POD / PDR</th>
                  <th>Mese</th>
                  <th>Cliente</th>
                  <th>Rappresentante</th>
                  <th>Agente</th>
                  <th>Nota</th>
                  <th>Esito</th>
                  <th>Data</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(det, idx) in dettagliPaginati" :key="idx">
                  <td>
                    <router-link
                      v-if="det.pod_code"
                      :to="`/ask/pods/${det.pod_id}/dettagli`"
                      target="_blank"
                      class="entity-link"
                      @click.stop
                    >{{ det.pod_code }}</router-link>
                    <router-link
                      v-else-if="det.pdr_code"
                      :to="`/ask/pdrs/${det.pdr_id}/dettagli`"
                      target="_blank"
                      class="entity-link"
                      @click.stop
                    >{{ det.pdr_code }}</router-link>
                    <span v-else>-</span>
                  </td>
                  <td class="mono">{{ det.mese }}</td>
                  <td>
                    <router-link
                      v-if="det.cliente_id"
                      :to="`/ask/clienti/${det.cliente_id}/dettagli`"
                      target="_blank"
                      class="entity-link"
                      @click.stop
                    >{{ det.cliente_nome }}</router-link>
                    <span v-else>-</span>
                  </td>
                  <td>
                    <router-link
                      v-if="det.rappresentante_id"
                      :to="`/ask/rappresentanti/${det.rappresentante_id}/dettagli`"
                      target="_blank"
                      class="entity-link"
                      @click.stop
                    >{{ det.rappresentante_nome }}</router-link>
                    <span v-else>-</span>
                  </td>
                  <td>
                    <router-link
                      v-if="det.agente_id"
                      :to="`/ask/agenti/${det.agente_id}/dettagli`"
                      target="_blank"
                      class="entity-link"
                      @click.stop
                    >{{ det.agente_nome }}</router-link>
                    <span v-else>-</span>
                  </td>
                  <td class="note-cell">{{ det.note || '-' }}</td>
                  <td>
                    <span v-if="det.esito == 1" class="status status-ok">Confermata</span>
                    <span v-else class="status status-danger">Respinta</span>
                  </td>
                  <td class="mono">{{ formatDate(det.data_fine) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="pagination" v-if="esplosoTotalPages > 0">
            <button type="button" @click="goToEsplosoPage(esplosoPage - 1)" :disabled="esplosoPage === 1">‹</button>
            <button
              v-for="page in visiblePages(esplosoPage, esplosoTotalPages)"
              :key="'e-' + page"
              type="button"
              @click="goToEsplosoPage(page)"
              :class="['page-btn', { active: page === esplosoPage }]"
            >
              {{ page }}
            </button>
            <button type="button" @click="goToEsplosoPage(esplosoPage + 1)" :disabled="esplosoPage === esplosoTotalPages">›</button>
            <span class="pagination-total">{{ dettagliFiltrati.length }} record</span>
          </div>
        </div>

        <!-- Esploso globale -->
        <div v-if="mostraTuttiDettagli" class="drilldown">
          <div class="drilldown-header">
            <div>
              <h3>Tutte le validazioni</h3>
              <p>Elenco completo sul filtro date corrente</p>
            </div>
            <button type="button" class="btn btn-ghost" @click="mostraTuttiDettagli = false">Chiudi</button>
          </div>
          <input
            v-model="searchEsplosoAll"
            class="search-input"
            type="text"
            placeholder="Cerca in tutte le validazioni..."
          />
          <div class="table-wrap">
            <table class="data-table compact">
              <thead>
                <tr>
                  <th>Validatore</th>
                  <th>POD / PDR</th>
                  <th>Mese</th>
                  <th>Cliente</th>
                  <th>Rappresentante</th>
                  <th>Agente</th>
                  <th>Nota</th>
                  <th>Esito</th>
                  <th>Data</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(det, idx) in tuttiDettagliFiltratiPaginati" :key="idx">
                  <td>
                    <div class="user-cell">
                      <strong>{{ det.nome }}</strong>
                      <span>{{ det.email }}</span>
                    </div>
                  </td>
                  <td>
                    <router-link
                      v-if="det.pod_code"
                      :to="`/ask/pods/${det.pod_id}/dettagli`"
                      target="_blank"
                      class="entity-link"
                    >{{ det.pod_code }}</router-link>
                    <router-link
                      v-else-if="det.pdr_code"
                      :to="`/ask/pdrs/${det.pdr_id}/dettagli`"
                      target="_blank"
                      class="entity-link"
                    >{{ det.pdr_code }}</router-link>
                    <span v-else>-</span>
                  </td>
                  <td class="mono">{{ det.mese }}</td>
                  <td>
                    <router-link
                      v-if="det.cliente_id"
                      :to="`/ask/clienti/${det.cliente_id}/dettagli`"
                      target="_blank"
                      class="entity-link"
                    >{{ det.cliente_nome }}</router-link>
                    <span v-else>-</span>
                  </td>
                  <td>
                    <router-link
                      v-if="det.rappresentante_id"
                      :to="`/ask/rappresentanti/${det.rappresentante_id}/dettagli`"
                      target="_blank"
                      class="entity-link"
                    >{{ det.rappresentante_nome }}</router-link>
                    <span v-else>-</span>
                  </td>
                  <td>
                    <router-link
                      v-if="det.agente_id"
                      :to="`/ask/agenti/${det.agente_id}/dettagli`"
                      target="_blank"
                      class="entity-link"
                    >{{ det.agente_nome }}</router-link>
                    <span v-else>-</span>
                  </td>
                  <td class="note-cell">{{ det.note || '-' }}</td>
                  <td>
                    <span v-if="det.esito == 1" class="status status-ok">Confermata</span>
                    <span v-else class="status status-danger">Respinta</span>
                  </td>
                  <td class="mono">{{ formatDate(det.data_fine) }}</td>
                </tr>
                <tr v-if="!tuttiDettagliFiltratiPaginati.length">
                  <td colspan="9" class="empty-cell">Nessun record trovato</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="pagination" v-if="esplosoAllTotalPages > 0">
            <button type="button" @click="goToEsplosoAllPage(esplosoAllPage - 1)" :disabled="esplosoAllPage === 1">‹</button>
            <button
              v-for="page in visiblePages(esplosoAllPage, esplosoAllTotalPages)"
              :key="'a-' + page"
              type="button"
              @click="goToEsplosoAllPage(page)"
              :class="['page-btn', { active: page === esplosoAllPage }]"
            >
              {{ page }}
            </button>
            <button type="button" @click="goToEsplosoAllPage(esplosoAllPage + 1)" :disabled="esplosoAllPage === esplosoAllTotalPages">›</button>
            <span class="pagination-total">{{ tuttiDettagliFiltrati.length }} record</span>
          </div>
        </div>
      </div>
    </section>

   
    <section v-else class="view-panel">
      <div class="panel filter-panel">
        <div class="panel-toolbar">
          <div>
            <h2>Filtro periodo</h2>
            <p class="panel-hint">Seleziona le date e clicca su un box per vedere il dettaglio.</p>
          </div>
          <div class="toolbar-actions period-filters">
            <label class="field">
              <span>Da</span>
              <Calendar
                v-model="statsDataDa"
                dateFormat="dd/mm/yy"
                :manualInput="false"
                showIcon
                placeholder="Data da"
              />
            </label>
            <label class="field">
              <span>A</span>
              <Calendar
                v-model="statsDataA"
                dateFormat="dd/mm/yy"
                :manualInput="false"
                showIcon
                placeholder="Data a"
              />
            </label>
            <button
              type="button"
              class="btn btn-primary"
              @click="loadStatistichePeriodo"
              :disabled="loaderStats"
            >
              {{ loaderStats ? 'Caricamento...' : 'Aggiorna' }}
            </button>
          </div>
        </div>
        <p class="period-chip">Periodo attivo: <strong>{{ statsPeriodoLabel }}</strong></p>
      </div>

      <div class="metrics metrics-clickable">
        <button
          type="button"
          class="metric"
          :class="{ selected: categoriaSelezionata === 'totale' }"
          @click="apriDettaglioCategoria('totale')"
        >
          <span class="metric-label">Totale fatture</span>
          <span class="metric-value">{{ formatNumber(statsPeriodo.totale_fatture) }}</span>
        </button>
        <button
          type="button"
          class="metric"
          :class="{ selected: categoriaSelezionata === 'validate' }"
          @click="apriDettaglioCategoria('validate')"
        >
          <span class="metric-label">Validate</span>
          <span class="metric-value">{{ formatNumber(statsPeriodo.totale_validate) }}</span>
        </button>
        <button
          type="button"
          class="metric tone-warn"
          :class="{ selected: categoriaSelezionata === 'non_ancora_validate' }"
          @click="apriDettaglioCategoria('non_ancora_validate')"
        >
          <span class="metric-label">Non validate</span>
          <span class="metric-value">{{ formatNumber(statsPeriodo.non_ancora_validate) }}</span>
        </button>
        <button
          type="button"
          class="metric tone-danger"
          :class="{ selected: categoriaSelezionata === 'respinte' }"
          @click="apriDettaglioCategoria('respinte')"
        >
          <span class="metric-label">Respinte</span>
          <span class="metric-value">{{ formatNumber(statsPeriodo.respinte) }}</span>
        </button>
        <button
          type="button"
          class="metric tone-ok"
          :class="{ selected: categoriaSelezionata === 'confermate' }"
          @click="apriDettaglioCategoria('confermate')"
        >
          <span class="metric-label">Confermate</span>
          <span class="metric-value">{{ formatNumber(statsPeriodo.confermate) }}</span>
        </button>
      </div>

      <div v-if="categoriaSelezionata" class="panel">
        <div class="panel-toolbar">
          <div>
            <h2>Dettaglio · {{ categoriaLabel }}</h2>
            <p class="panel-hint">{{ dettaglioPeriodoFiltrato.length }} risultati</p>
          </div>
          <div class="toolbar-actions">
            <input
              v-model="searchDettaglioPeriodo"
              class="search-input"
              type="text"
              placeholder="Cerca nel dettaglio..."
            />
            <button type="button" class="btn btn-ghost" @click="chiudiDettaglioCategoria">Chiudi</button>
          </div>
        </div>

        <div v-if="loaderDettaglioStats" class="loading-state">Caricamento dettaglio...</div>

        <template v-else>
          <div class="table-wrap">
            <table class="data-table compact">
              <thead>
                <tr>
                  <th>Validatore</th>
                  <th>POD / PDR</th>
                  <th>N. Fattura</th>
                  <th>Tipo</th>
                  <th>Mese</th>
                  <th>Cliente</th>
                  <th>Rappresentante</th>
                  <th>Agente</th>
                  <th>Totale</th>
                  <th>kWh/Smc</th>
                  <th>Nota</th>
                  <th>Esito</th>
                  <th>Data fine</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="det in dettaglioPeriodoPaginato" :key="det.id">
                  <td>{{ det.validatore?.nome || '-' }}</td>
                  <td>
                    <router-link
                      v-if="det.pod_code"
                      :to="`/ask/pods/${det.pod_id}/dettagli`"
                      target="_blank"
                      class="entity-link"
                    >{{ det.pod_code }}</router-link>
                    <router-link
                      v-else-if="det.pdr_code"
                      :to="`/ask/pdrs/${det.pdr_id}/dettagli`"
                      target="_blank"
                      class="entity-link"
                    >{{ det.pdr_code }}</router-link>
                    <span v-else>-</span>
                  </td>
                  <td>
                    <router-link
                      v-if="det.numero_fattura"
                      :to="`/ask/clienti/fatture/${det.numero_fattura}/dettagli`"
                      target="_blank"
                      class="entity-link"
                    >{{ det.numero_fattura }}</router-link>
                    <span v-else>-</span>
                  </td>
                  <td>{{ det.tipo || '-' }}</td>
                  <td class="mono">{{ det.mese || '-' }}</td>
                  <td>
                    <router-link
                      v-if="det.cliente_id"
                      :to="`/ask/clienti/${det.cliente_id}/dettagli`"
                      target="_blank"
                      class="entity-link"
                    >{{ det.cliente_nome }}</router-link>
                    <span v-else>-</span>
                  </td>
                  <td>
                    <router-link
                      v-if="det.rappresentante_id"
                      :to="`/ask/rappresentanti/${det.rappresentante_id}/dettagli`"
                      target="_blank"
                      class="entity-link"
                    >{{ det.rappresentante_nome }}</router-link>
                    <span v-else>-</span>
                  </td>
                  <td>
                    <router-link
                      v-if="det.agente_id"
                      :to="`/ask/agenti/${det.agente_id}/dettagli`"
                      target="_blank"
                      class="entity-link"
                    >{{ det.agente_nome }}</router-link>
                    <span v-else>-</span>
                  </td>
                  <td class="mono">{{ formatCurrency(det.totale) }}</td>
                  <td class="mono">{{ formatNumber(det.tot_kwh) }}</td>
                  <td class="note-cell">{{ det.note || '-' }}</td>
                  <td>
                    <span v-if="det.data_fine == null" class="status status-muted">Non validata</span>
                    <span v-else-if="det.esito == 1" class="status status-ok">Confermata</span>
                    <span v-else-if="det.esito == 0" class="status status-danger">Respinta</span>
                    <span v-else>-</span>
                  </td>
                  <td class="mono">{{ formatDate(det.data_fine) || '-' }}</td>
                </tr>
                <tr v-if="!dettaglioPeriodoFiltrato.length">
                  <td colspan="13" class="empty-cell">Nessun record trovato</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="pagination" v-if="dettaglioPeriodoTotalPages > 0">
            <button type="button" @click="goToDettaglioPeriodoPage(dettaglioPeriodoPage - 1)" :disabled="dettaglioPeriodoPage === 1">‹</button>
            <button
              v-for="page in visiblePages(dettaglioPeriodoPage, dettaglioPeriodoTotalPages)"
              :key="'dp-' + page"
              type="button"
              @click="goToDettaglioPeriodoPage(page)"
              :class="['page-btn', { active: page === dettaglioPeriodoPage }]"
            >
              {{ page }}
            </button>
            <button
              type="button"
              @click="goToDettaglioPeriodoPage(dettaglioPeriodoPage + 1)"
              :disabled="dettaglioPeriodoPage === dettaglioPeriodoTotalPages"
            >›</button>
            <span class="pagination-total">{{ dettaglioPeriodoFiltrato.length }} record</span>
          </div>
        </template>
      </div>

      <div v-else class="empty-prompt">
        Seleziona una metrica sopra per aprire il dettaglio fatture.
      </div>
    </section>
  </div>
</template>

<script>
import Service from '@/services/ask-fatturazione/fatturazione-service'
import { ref, getCurrentInstance, onMounted, computed, watch } from 'vue';
import { useToast } from "primevue/usetoast";
import Loader from '@/components/Loader.vue'
import Calendar from 'primevue/calendar';

export default {
  components: { Loader, Calendar },
  setup() {
    const loader = ref(false);
    const self = getCurrentInstance();
    self.$eventBus = self.appContext.config.globalProperties.$eventBus;
    self.$http = self.appContext.config.globalProperties.$http;
    const service = new Service(self.$http, self.$eventBus);
    const toast = useToast();

    const activeView = ref('attivita');

    const totaleFatture = ref(0);
    const totaleValidate = ref(0);
    const nonAncoraValidate = ref(0);
    const validateEsito0 = ref(0);
    const validateEsito1 = ref(0);
    const mediaValidazioniGiorno = ref(0);
    const dettaglio = ref([]);

    const dateRange = ref(null);
    const sortAsc = ref(true);
    const sortKey = ref('data');
    const currentPage = ref(1);
    const pageSize = 20;

    const formatDateForFilter = (dateObj) => {
      if (!dateObj) return '';
      const d = new Date(dateObj);
      const month = '' + (d.getMonth() + 1);
      const day = '' + d.getDate();
      const year = d.getFullYear();
      return [year, month.padStart(2, '0'), day.padStart(2, '0')].join('-');
    };

    const dettaglioFiltrato = computed(() => {
      let risultati = [];
      dettaglio.value.forEach(utente => {
        utente.giorni.forEach(giorno => {
          risultati.push({
            ...giorno,
            user_id: utente.user_id,
            nome: utente.nome,
            email: utente.email
          });
        });
      });

      if (dateRange.value && dateRange.value[0]) {
        const startStr = formatDateForFilter(dateRange.value[0]);
        const endStr = dateRange.value[1] ? formatDateForFilter(dateRange.value[1]) : startStr;
        risultati = risultati.filter(r => {
          const dataGiorno = (r.data || '').split(/[T ]/)[0];
          return dataGiorno >= startStr && dataGiorno <= endStr;
        });
      }

      risultati.sort((a, b) => {
        if (sortKey.value === 'data') {
          return sortAsc.value
            ? a.data.localeCompare(b.data)
            : b.data.localeCompare(a.data);
        }
        if (sortKey.value === 'n_fatture_validate') {
          return sortAsc.value
            ? a.n_fatture_validate - b.n_fatture_validate
            : b.n_fatture_validate - a.n_fatture_validate;
        }
        return 0;
      });

      return risultati;
    });

    const setSort = (key) => {
      if (sortKey.value === key) {
        sortAsc.value = !sortAsc.value;
      } else {
        sortKey.value = key;
        sortAsc.value = true;
      }
    };

    const dettaglioPaginato = computed(() => {
      const start = (currentPage.value - 1) * pageSize;
      return dettaglioFiltrato.value.slice(start, start + pageSize);
    });

    const totalPages = computed(() => Math.ceil(dettaglioFiltrato.value.length / pageSize));

    const goToPage = (page) => {
      if (page >= 1 && page <= totalPages.value) currentPage.value = page;
    };

    const visiblePages = (current, total) => {
      if (!total || total < 1) return [];
      const max = 7;
      if (total <= max) return Array.from({ length: total }, (_, i) => i + 1);
      let start = Math.max(1, current - 3);
      let end = Math.min(total, start + max - 1);
      start = Math.max(1, end - max + 1);
      return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    };

    const loadData = async () => {
      try {
        loader.value = true;
        const response = await service.getInfoValidazioniFatture();
        if (response.success) {
          totaleFatture.value = response.totale_validazioni;
          totaleValidate.value = response.totale_validate;
          nonAncoraValidate.value = response.non_ancora_validate;
          validateEsito0.value = response.validate_esito_0;
          validateEsito1.value = response.validate_esito_1;
          mediaValidazioniGiorno.value = response.media_validazioni_giorno;
          dettaglio.value = response.dettaglio_per_utente;
        } else {
          toast.add({ severity: 'error', summary: 'Errore', detail: response.message, life: 3000 });
        }
      } catch (error) {
        toast.add({ severity: 'error', summary: 'Errore', detail: 'Errore nel caricamento dei dati', life: 3000 });
      } finally {
        loader.value = false;
      }
    };

    const formatDate = (dateStr) => {
      if (!dateStr) return '';
      const datePart = dateStr.split(' ')[0].split('T')[0];
      const [year, month, day] = datePart.split('-');
      return `${day}/${month}/${year}`;
    };

    const toggleSort = () => {
      sortAsc.value = !sortAsc.value;
    };

    const dettaglioSelezionato = ref(null);
    const mostraDettaglio = (row) => {
      if (dettaglioSelezionato.value && dettaglioSelezionato.value.row === row) {
        dettaglioSelezionato.value = null;
      } else {
        dettaglioSelezionato.value = { row, dettagli: row.dettagli || [] };
        mostraTuttiDettagli.value = false;
      }
    };

    const esplosoPage = ref(1);
    const esplosoPageSize = 20;

    const goToEsplosoPage = (page) => {
      if (page >= 1 && page <= esplosoTotalPages.value) esplosoPage.value = page;
    };
    watch(dettaglioSelezionato, () => { esplosoPage.value = 1; });
    watch(dateRange, () => { currentPage.value = 1; });

    const mostraTuttiDettagli = ref(false);
    const toggleMostraTutto = () => {
      mostraTuttiDettagli.value = !mostraTuttiDettagli.value;
      if (mostraTuttiDettagli.value) dettaglioSelezionato.value = null;
    };

    const tuttiDettagli = computed(() => {
      let all = [];
      dettaglioFiltrato.value.forEach(row => {
        if (row.dettagli && row.dettagli.length) {
          all = all.concat(row.dettagli.map(det => ({
            ...det,
            nome: row.nome,
            email: row.email,
            data: row.data
          })));
        }
      });
      return all;
    });

    const searchEsploso = ref('');
    const searchEsplosoAll = ref('');

    const dettagliFiltrati = computed(() => {
      if (!dettaglioSelezionato.value) return [];
      if (!searchEsploso.value) return dettaglioSelezionato.value.dettagli;
      const q = searchEsploso.value.toLowerCase();
      return dettaglioSelezionato.value.dettagli.filter(det =>
        Object.values(det).some(val => val && val.toString().toLowerCase().includes(q))
      );
    });

    const dettagliPaginati = computed(() => {
      const start = (esplosoPage.value - 1) * esplosoPageSize;
      return dettagliFiltrati.value.slice(start, start + esplosoPageSize);
    });

    const esplosoTotalPages = computed(() =>
      Math.ceil((dettagliFiltrati.value?.length || 0) / esplosoPageSize)
    );

    const tuttiDettagliFiltrati = computed(() => {
      if (!searchEsplosoAll.value) return tuttiDettagli.value;
      const q = searchEsplosoAll.value.toLowerCase();
      return tuttiDettagli.value.filter(det =>
        Object.values(det).some(val => val && val.toString().toLowerCase().includes(q))
      );
    });

    const esplosoAllPage = ref(1);
    const esplosoAllPageSize = 50;

    const tuttiDettagliFiltratiPaginati = computed(() => {
      const start = (esplosoAllPage.value - 1) * esplosoAllPageSize;
      return tuttiDettagliFiltrati.value.slice(start, start + esplosoAllPageSize);
    });

    const esplosoAllTotalPages = computed(() =>
      Math.ceil(tuttiDettagliFiltrati.value.length / esplosoAllPageSize)
    );

    const goToEsplosoAllPage = (page) => {
      if (page >= 1 && page <= esplosoAllTotalPages.value) esplosoAllPage.value = page;
    };

    watch(mostraTuttiDettagli, (val) => {
      if (val) esplosoAllPage.value = 1;
    });

    const now = new Date();
    const statsDataDa = ref(new Date(now.getFullYear(), now.getMonth(), 1));
    const statsDataA = ref(new Date(now.getFullYear(), now.getMonth() + 1, 0));
    const loaderStats = ref(false);
    const loaderDettaglioStats = ref(false);
    const statsPeriodo = ref({
      totale_fatture: 0,
      totale_validate: 0,
      non_ancora_validate: 0,
      respinte: 0,
      confermate: 0,
      data_da: '',
      data_a: '',
    });
    const categoriaSelezionata = ref(null);
    const dettaglioPeriodo = ref([]);
    const searchDettaglioPeriodo = ref('');
    const dettaglioPeriodoPage = ref(1);
    const dettaglioPeriodoPageSize = 20;
    const statsLoadedOnce = ref(false);

    const formatDataParam = (dateObj) => {
      if (!dateObj) return '';
      const d = new Date(dateObj);
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      return `${day}-${month}-${d.getFullYear()}`;
    };

    const getDataParams = () => ({
      data_da: formatDataParam(statsDataDa.value),
      data_a: formatDataParam(statsDataA.value),
    });

    const statsPeriodoLabel = computed(() => {
      const da = statsPeriodo.value.data_da || formatDataParam(statsDataDa.value);
      const a = statsPeriodo.value.data_a || formatDataParam(statsDataA.value);
      if (!da && !a) return '-';
      return da === a ? da : `${da} → ${a}`;
    });

    const categoriaLabels = {
      totale: 'Totale Fatture',
      validate: 'Totale Validate',
      non_ancora_validate: 'Non Ancora Validate',
      respinte: 'Respinte',
      confermate: 'Confermate',
    };

    const categoriaLabel = computed(() => categoriaLabels[categoriaSelezionata.value] || '');

    const formatCurrency = (value) => {
      if (value === null || value === undefined || value === '') return '-';
      return new Intl.NumberFormat('it-IT', {
        style: 'currency',
        currency: 'EUR',
      }).format(Number(value) || 0);
    };

    const formatNumber = (value) => {
      if (value === null || value === undefined || value === '') return '-';
      return new Intl.NumberFormat('it-IT').format(Number(value) || 0);
    };

    const loadStatistichePeriodo = async () => {
      const { data_da, data_a } = getDataParams();
      if (!data_da || !data_a) {
        toast.add({
          severity: 'warn',
          summary: 'Attenzione',
          detail: 'Seleziona un intervallo date valido',
          life: 3000,
        });
        return;
      }

      loaderStats.value = true;
      try {
        const response = await service.getStatisticheValidazioniFatture({ data_da, data_a });
        if (response.success) {
          statsPeriodo.value = {
            totale_fatture: response.totale_fatture || 0,
            totale_validate: response.totale_validate || 0,
            non_ancora_validate: response.non_ancora_validate || 0,
            respinte: response.respinte || 0,
            confermate: response.confermate || 0,
            data_da: response.data_da || data_da,
            data_a: response.data_a || data_a,
          };
          statsLoadedOnce.value = true;
          if (categoriaSelezionata.value) {
            await apriDettaglioCategoria(categoriaSelezionata.value, false);
          }
        } else {
          toast.add({
            severity: 'error',
            summary: 'Errore',
            detail: response.message || 'Errore nel caricamento delle statistiche',
            life: 3000,
          });
        }
      } catch (error) {
        toast.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Errore nel caricamento delle statistiche per periodo',
          life: 3000,
        });
      } finally {
        loaderStats.value = false;
      }
    };

    const switchToPeriodo = () => {
      activeView.value = 'periodo';
      if (!statsLoadedOnce.value) loadStatistichePeriodo();
    };

    const apriDettaglioCategoria = async (categoria, toggle = true) => {
      if (toggle && categoriaSelezionata.value === categoria) {
        chiudiDettaglioCategoria();
        return;
      }

      categoriaSelezionata.value = categoria;
      searchDettaglioPeriodo.value = '';
      dettaglioPeriodoPage.value = 1;
      loaderDettaglioStats.value = true;

      try {
        const { data_da, data_a } = getDataParams();
        const response = await service.getDettaglioStatisticheValidazioniFatture({
          categoria,
          data_da,
          data_a,
        });
        if (response.success) {
          dettaglioPeriodo.value = response.fatture || [];
        } else {
          dettaglioPeriodo.value = [];
          toast.add({
            severity: 'error',
            summary: 'Errore',
            detail: response.message || 'Errore nel caricamento del dettaglio',
            life: 3000,
          });
        }
      } catch (error) {
        dettaglioPeriodo.value = [];
        toast.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Errore nel caricamento del dettaglio',
          life: 3000,
        });
      } finally {
        loaderDettaglioStats.value = false;
      }
    };

    const chiudiDettaglioCategoria = () => {
      categoriaSelezionata.value = null;
      dettaglioPeriodo.value = [];
      searchDettaglioPeriodo.value = '';
      dettaglioPeriodoPage.value = 1;
    };

    const flattenSearchable = (det) => {
      const values = [
        det.validatore?.nome,
        det.validatore?.email,
        det.pod_code,
        det.pdr_code,
        det.numero_fattura,
        det.tipo,
        det.mese,
        det.cliente_nome,
        det.rappresentante_nome,
        det.agente_nome,
        det.note,
        det.esito,
        det.totale,
        det.tot_kwh,
        det.data_fine,
      ];
      return values
        .filter((v) => v !== null && v !== undefined)
        .map((v) => v.toString().toLowerCase())
        .join(' ');
    };

    const dettaglioPeriodoFiltrato = computed(() => {
      if (!searchDettaglioPeriodo.value) return dettaglioPeriodo.value;
      const q = searchDettaglioPeriodo.value.toLowerCase();
      return dettaglioPeriodo.value.filter((det) => flattenSearchable(det).includes(q));
    });

    const dettaglioPeriodoPaginato = computed(() => {
      const start = (dettaglioPeriodoPage.value - 1) * dettaglioPeriodoPageSize;
      return dettaglioPeriodoFiltrato.value.slice(start, start + dettaglioPeriodoPageSize);
    });

    const dettaglioPeriodoTotalPages = computed(() =>
      Math.ceil(dettaglioPeriodoFiltrato.value.length / dettaglioPeriodoPageSize)
    );

    const goToDettaglioPeriodoPage = (page) => {
      if (page >= 1 && page <= dettaglioPeriodoTotalPages.value) {
        dettaglioPeriodoPage.value = page;
      }
    };

    watch(searchDettaglioPeriodo, () => {
      dettaglioPeriodoPage.value = 1;
    });

    onMounted(loadData);

    return {
      loader,
      activeView,
      switchToPeriodo,
      totaleFatture,
      totaleValidate,
      nonAncoraValidate,
      validateEsito0,
      validateEsito1,
      mediaValidazioniGiorno,
      dettaglio,
      formatDate,
      dateRange,
      dettaglioFiltrato,
      dettaglioPaginato,
      toggleSort,
      sortAsc,
      currentPage,
      pageSize,
      totalPages,
      goToPage,
      setSort,
      sortKey,
      mostraDettaglio,
      dettaglioSelezionato,
      esplosoPage,
      esplosoPageSize,
      dettagliPaginati,
      esplosoTotalPages,
      goToEsplosoPage,
      mostraTuttiDettagli,
      toggleMostraTutto,
      tuttiDettagli,
      searchEsploso,
      searchEsplosoAll,
      dettagliFiltrati,
      tuttiDettagliFiltrati,
      esplosoAllPage,
      esplosoAllPageSize,
      tuttiDettagliFiltratiPaginati,
      esplosoAllTotalPages,
      goToEsplosoAllPage,
      statsDataDa,
      statsDataA,
      loaderStats,
      loaderDettaglioStats,
      statsPeriodo,
      statsPeriodoLabel,
      categoriaSelezionata,
      categoriaLabel,
      loadStatistichePeriodo,
      apriDettaglioCategoria,
      chiudiDettaglioCategoria,
      searchDettaglioPeriodo,
      dettaglioPeriodoFiltrato,
      dettaglioPeriodoPaginato,
      dettaglioPeriodoPage,
      dettaglioPeriodoTotalPages,
      goToDettaglioPeriodoPage,
      formatCurrency,
      formatNumber,
      visiblePages,
    };
  }
}
</script>

<style scoped>
.page {
  --ink: #1a2b3c;
  --muted: #6b7c8f;
  --line: #d7e0ea;
  --surface: #ffffff;
  --bg: #f3f6f9;
  --accent: #0f5889;
  --accent-soft: #e8f2f8;
  --ok: #1f7a5c;
  --ok-bg: #e8f6f0;
  --warn: #9a6b16;
  --warn-bg: #fff6e5;
  --danger: #b42318;
  --danger-bg: #fdeceb;
  max-width: 1440px;
  margin: 0 auto;
  padding: 28px 24px 48px;
  min-height: 100dvh;
  color: var(--ink);
  background: var(--bg);
}

.page-header h1 {
  margin: 4px 0 8px;
  font-size: 1.85rem;
  font-weight: 650;
  letter-spacing: -0.03em;
  line-height: 1.15;
}

.eyebrow {
  margin: 0;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--accent);
}

.lede {
  margin: 0;
  color: var(--muted);
  max-width: 52ch;
  line-height: 1.5;
}

.view-tabs {
  display: inline-flex;
  gap: 4px;
  margin: 28px 0 20px;
  padding: 4px;
  background: #e6edf3;
  border-radius: 10px;
}

.view-tab {
  border: none;
  background: transparent;
  color: var(--muted);
  font-weight: 600;
  font-size: 0.95rem;
  padding: 10px 18px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s cubic-bezier(0.16, 1, 0.3, 1), color 0.2s ease, transform 0.15s ease;
}

.view-tab:hover {
  color: var(--ink);
}

.view-tab.active {
  background: var(--surface);
  color: var(--accent);
  box-shadow: 0 1px 3px rgba(15, 88, 137, 0.12);
}

.view-tab:active {
  transform: scale(0.98);
}

.view-panel {
  animation: fadeIn 0.25s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
}

.metrics {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 18px;
}

.metrics-clickable {
  grid-template-columns: repeat(5, minmax(0, 1fr));
}

.metric {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: left;
  border-left: 3px solid var(--accent);
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease;
}

button.metric {
  cursor: pointer;
  font: inherit;
  color: inherit;
}

button.metric:hover {
  box-shadow: 0 4px 14px rgba(26, 43, 60, 0.08);
  transform: translateY(-1px);
}

button.metric:active {
  transform: scale(0.98);
}

button.metric.selected {
  background: var(--accent-soft);
  border-color: var(--accent);
  box-shadow: inset 0 0 0 1px var(--accent);
}

.metric.tone-ok { border-left-color: var(--ok); }
.metric.tone-warn { border-left-color: var(--warn); }
.metric.tone-danger { border-left-color: var(--danger); }

.metric-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--muted);
  letter-spacing: 0.01em;
}

.metric-value {
  font-size: 1.45rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.02em;
  line-height: 1.1;
}

.panel {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 12px;
  overflow: hidden;
}

.filter-panel {
  margin-bottom: 18px;
}

.panel-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  padding: 18px 20px;
  border-bottom: 1px solid var(--line);
  flex-wrap: wrap;
}

.panel-toolbar h2,
.drilldown-header h3 {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 650;
  letter-spacing: -0.02em;
}

.panel-hint,
.drilldown-header p {
  margin: 4px 0 0;
  color: var(--muted);
  font-size: 0.88rem;
}

.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.period-filters .field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--muted);
}

.period-chip {
  margin: 0;
  padding: 12px 20px 16px;
  color: var(--muted);
  font-size: 0.9rem;
}

.period-chip strong {
  color: var(--ink);
  font-variant-numeric: tabular-nums;
}

.btn {
  border: none;
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, transform 0.15s ease;
}

.btn:active {
  transform: scale(0.98);
}

.btn-primary {
  background: var(--accent);
  color: #fff;
}

.btn-primary:hover:not(:disabled) {
  background: #0c476f;
}

.btn-primary:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.btn-secondary {
  background: var(--accent-soft);
  color: var(--accent);
}

.btn-secondary:hover,
.btn-secondary.active {
  background: var(--accent);
  color: #fff;
}

.btn-ghost {
  background: transparent;
  color: var(--muted);
  border: 1px solid var(--line);
}

.btn-ghost:hover {
  color: var(--ink);
  border-color: #b8c5d3;
}

.table-wrap {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table th,
.data-table td {
  padding: 12px 16px;
  text-align: left;
  border-bottom: 1px solid var(--line);
  vertical-align: middle;
}

.data-table th {
  background: #f7fafc;
  color: var(--muted);
  font-size: 0.75rem;
  font-weight: 650;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
}

.data-table.compact th,
.data-table.compact td {
  padding: 10px 12px;
  font-size: 0.9rem;
}

.data-table tbody tr {
  background: var(--surface);
  transition: background 0.15s ease;
}

.data-table tbody tr:hover {
  background: #f8fbfd;
}

.data-table tbody tr.selected {
  background: var(--accent-soft);
}

.data-table tbody tr[class],
.panel .data-table tbody tr {
  cursor: default;
}

.panel > .table-wrap > .data-table tbody tr {
  cursor: pointer;
}

.sortable {
  cursor: pointer;
  user-select: none;
}

.count-pill {
  display: inline-flex;
  min-width: 2rem;
  justify-content: center;
  padding: 3px 10px;
  border-radius: 999px;
  background: var(--accent-soft);
  color: var(--accent);
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.user-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.user-cell strong {
  font-weight: 600;
}

.user-cell span {
  color: var(--muted);
  font-size: 0.82rem;
}

.mono {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.note-cell {
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.entity-link {
  color: var(--accent);
  font-weight: 600;
  text-decoration: none;
}

.entity-link:hover {
  text-decoration: underline;
}

.status {
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 0.78rem;
  font-weight: 650;
}

.status-ok {
  background: var(--ok-bg);
  color: var(--ok);
}

.status-danger {
  background: var(--danger-bg);
  color: var(--danger);
}

.status-muted {
  background: #eef2f6;
  color: var(--muted);
}

.empty-cell,
.empty-prompt,
.loading-state {
  text-align: center;
  padding: 28px 16px;
  color: var(--muted);
}

.empty-prompt {
  background: var(--surface);
  border: 1px dashed var(--line);
  border-radius: 12px;
}

.pagination {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 14px 16px;
  flex-wrap: wrap;
  border-top: 1px solid var(--line);
}

.pagination button {
  min-width: 32px;
  height: 32px;
  border: 1px solid var(--line);
  background: var(--surface);
  color: var(--ink);
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.pagination button:hover:not(:disabled) {
  border-color: var(--accent);
  color: var(--accent);
}

.pagination button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.page-btn.active {
  background: var(--accent);
  border-color: var(--accent);
  color: #fff;
}

.pagination-total {
  margin-left: 8px;
  color: var(--muted);
  font-size: 0.88rem;
}

.drilldown {
  border-top: 1px solid var(--line);
  background: #fafcfd;
  padding: 16px 20px 8px;
}

.drilldown-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
}

.search-input {
  width: min(320px, 100%);
  border: 1px solid var(--line);
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 0.92rem;
  margin-bottom: 12px;
  background: var(--surface);
}

.search-input:focus {
  outline: none;
  border-color: var(--accent);
  box-shadow: 0 0 0 3px rgba(15, 88, 137, 0.12);
}

.filter-calendar :deep(.p-inputtext) {
  width: 220px;
}

@media (max-width: 1100px) {
  .metrics,
  .metrics-clickable {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 720px) {
  .page {
    padding: 20px 14px 40px;
  }

  .metrics,
  .metrics-clickable {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .view-tabs {
    display: flex;
    width: 100%;
  }

  .view-tab {
    flex: 1;
    text-align: center;
    padding: 10px 8px;
    font-size: 0.85rem;
  }
}
</style>
